import { useEffect, useRef, useState } from "react";
import { cdSubscribe, cdSubscribeRemote } from "@/lib/customerDisplay";
import { BrandLogo, FirmLogo } from "@/components/BrandLogo";
import { fmtTL } from "@/components/StatusBadge";
import { ShoppingCart, Banknote, CreditCard, CheckCircle2, Store, DoorClosed, Users, Minus, Plus } from "lucide-react";

const SYMBOL = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };

function RegisterDisplay() {
  const [msg, setMsg] = useState({ mode: "idle" });
  const [store, setStore] = useState("Groot Market");
  const [done, setDone] = useState(null);
  const doneTimer = useRef(null);

  useEffect(() => {
    const handle = (s) => {
      if (!s) return;
      if (s.store) setStore(s.store);
      if (s.mode === "done") {
        setDone(s);
        clearTimeout(doneTimer.current);
        doneTimer.current = setTimeout(() => setDone(null), 7000);
      } else {
        // Yeni sepet başlarsa "Teşekkürler" ekranını hemen kaldır (7sn bekleme)
        if (s.mode === "cart" && (s.lines || []).length > 0) {
          setDone(null);
          clearTimeout(doneTimer.current);
        }
        setMsg(s);
      }
    };
    const unsub = cdSubscribe(handle);
    const k = new URLSearchParams(window.location.search).get("k");
    const unsubR = k ? cdSubscribeRemote(k, handle) : null;
    return () => { unsub && unsub(); unsubR && unsubR(); clearTimeout(doneTimer.current); };
  }, []);

  const view = done || msg;

  return (
    <div data-testid="customer-display" className="flex min-h-screen flex-col bg-slate-800 text-white select-none">
      {/* Marka başlığı */}
      <header className="flex items-center justify-between border-b border-white/10 px-8 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-green">
            <ShoppingCart className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="font-heading text-2xl font-black leading-none tracking-tight">Groot <span className="text-brand-orange">ONE</span></p>
            <p className="mt-1 text-xs uppercase tracking-[0.25em] text-slate-400">Müşteri Ekranı</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 text-sm text-slate-300">
          <Store className="h-4 w-4 text-brand-green" /> <span data-testid="cd-store">{store}</span>
        </div>
      </header>

      <main className="flex flex-1 flex-col overflow-hidden px-8 py-6">
        {done ? <DoneView s={done} /> : view.mode === "pay" ? <PayView s={view} /> : view.mode === "cart" ? <CartView s={view} /> : <IdleView store={store} />}
      </main>
    </div>
  );
}

function IdleView({ store }) {
  return (
    <div data-testid="cd-idle" className="flex flex-1 flex-col items-center justify-center text-center">
      <div className="-mt-12 mb-6 rounded-2xl bg-white px-6 py-3.5 shadow-2xl">
        <FirmLogo size={42} />
      </div>
      <ShoppingCart className="h-16 w-16 text-brand-green/60" />
      <h1 className="mt-5 font-heading text-5xl font-black">Hoş Geldiniz</h1>
      <p className="mt-3 text-xl text-slate-400">{store} sizi ağırlamaktan mutluluk duyar</p>
      <p className="mt-10 text-sm uppercase tracking-[0.3em] text-slate-600">Kasa hazır · lütfen bekleyiniz</p>
    </div>
  );
}

function CartView({ s }) {
  const lines = s.lines || [];
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {s.customer && <p className="mb-2 text-sm text-slate-400">Müşteri: <b className="text-white">{s.customer}</b></p>}
      <div data-testid="cd-cart-lines" className="flex-1 space-y-1 overflow-y-auto pr-1">
        {lines.length === 0 && <p className="py-10 text-center text-slate-500">Sepet boş</p>}
        {lines.map((l, i) => (
          <div key={i} data-testid={`cd-line-${i}`} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-5 py-3">
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold">{l.name}</p>
              <p className="text-sm text-slate-400">{l.qty} {l.unit || "adet"} × {fmtTL(l.unit_price)}</p>
            </div>
            <p className="tabular ml-4 shrink-0 font-heading text-xl font-bold">{fmtTL(l.line_total)}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 border-t border-white/10 pt-4">
        {s.discount > 0 && (
          <div className="mb-1 flex items-center justify-between text-lg text-brand-orange">
            <span>İndirim</span><span className="tabular">− {fmtTL(s.discount)}</span>
          </div>
        )}
        <div className="flex items-end justify-between">
          <span className="text-xl font-semibold text-slate-300">TOPLAM</span>
          <span data-testid="cd-total" className="tabular font-heading text-6xl font-black text-brand-green">{fmtTL(s.total)}</span>
        </div>
      </div>
    </div>
  );
}

function PayView({ s }) {
  const collected = s.collected || [];
  const paidDone = s.remaining <= 0.01;
  return (
    <div className="flex flex-1 flex-col">
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-2xl bg-brand-green/15 p-6 text-center ring-1 ring-inset ring-brand-green/30">
          <p className="text-sm uppercase tracking-widest text-emerald-200/80">Toplam Tutar</p>
          <p data-testid="cd-pay-total" className="tabular mt-1 font-heading text-5xl font-black text-brand-green">{fmtTL(s.total)}</p>
        </div>
        <div className={`rounded-2xl p-6 text-center ring-1 ring-inset ${paidDone ? "bg-emerald-500/15 ring-emerald-400/30" : "bg-red-500/15 ring-red-400/30"}`}>
          <p className="text-sm uppercase tracking-widest text-slate-300/80">Kalan</p>
          <p data-testid="cd-pay-remaining" className={`tabular mt-1 font-heading text-5xl font-black ${paidDone ? "text-emerald-400" : "text-red-400"}`}>{fmtTL(s.remaining)}</p>
        </div>
      </div>

      <div className="mt-5 flex-1 space-y-2 overflow-y-auto">
        {collected.map((c, i) => (
          <div key={i} data-testid={`cd-collected-${i}`} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-5 py-3 text-lg">
            <span className="inline-flex items-center gap-2 font-semibold text-slate-200">
              {c.method === "card" ? <CreditCard className="h-5 w-5 text-sky-400" /> : <Banknote className="h-5 w-5 text-emerald-400" />}
              {c.method === "card" ? "Kart" : "Nakit"}
              {c.currency && c.currency !== "TRY" && <span className="text-sm text-slate-400">({c.amount} {SYMBOL[c.currency]})</span>}
            </span>
            <span className="tabular font-bold">{fmtTL(c.amountTry)}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-end justify-between rounded-2xl bg-brand-orange/15 px-6 py-5 ring-1 ring-inset ring-brand-orange/30">
        <span className="text-xl font-semibold text-orange-200">Para Üstü</span>
        <span data-testid="cd-pay-change" className="tabular font-heading text-5xl font-black text-brand-orange">{fmtTL(s.change || 0)}</span>
      </div>
    </div>
  );
}

function DoneView({ s }) {
  return (
    <div data-testid="cd-done" className="flex flex-1 flex-col items-center justify-center text-center">
      <CheckCircle2 className="h-24 w-24 text-brand-green" />
      <h1 className="mt-6 font-heading text-5xl font-black">Teşekkürler!</h1>
      <p className="mt-2 text-lg text-slate-400">Yine bekleriz · Fiş No: <b className="text-white">{s.sale_no}</b></p>
      {s.change > 0 && (
        <div className="mt-8 rounded-2xl bg-brand-orange/15 px-10 py-6 ring-1 ring-inset ring-brand-orange/30">
          <p className="text-sm uppercase tracking-widest text-orange-200">Para Üstünüz</p>
          <p data-testid="cd-done-change" className="tabular mt-1 font-heading text-6xl font-black text-brand-orange">{fmtTL(s.change)}</p>
        </div>
      )}
    </div>
  );
}

function CheckoutBoard() {
  const base = process.env.REACT_APP_BACKEND_URL;
  const urlLoc = new URLSearchParams(window.location.search).get("loc") || "";
  const [loc, setLoc] = useState(urlLoc);
  const [stores, setStores] = useState([]);
  const [d, setD] = useState({ checkouts: [], available: 0, busy: 0, total: 0, waiting: null, store_name: null });

  useEffect(() => {
    fetch(`${base}/api/checkouts/stores`, { cache: "no-store" }).then((r) => r.json()).then((j) => setStores(j.stores || [])).catch(() => {});
  }, [base]);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const r = await fetch(`${base}/api/checkouts/board${loc ? `?location_id=${encodeURIComponent(loc)}` : ""}`, { cache: "no-store" });
        if (r.ok) { const j = await r.json(); if (!stop) setD(j); }
      } catch (e) {}
      if (!stop) setTimeout(tick, 3000);
    };
    tick();
    return () => { stop = true; };
  }, [base, loc]);

  const adjustWaiting = async (delta) => {
    if (!loc) return;
    try {
      const r = await fetch(`${base}/api/checkouts/queue`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ location_id: loc, delta }) });
      if (r.ok) { const j = await r.json(); setD((p) => ({ ...p, waiting: j.waiting })); }
    } catch (e) {}
  };

  return (
    <div data-testid="checkout-board" className="flex min-h-screen flex-col select-none bg-slate-900 text-white">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-8 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-green"><Store className="h-6 w-6 text-white" /></div>
          <div>
            <p className="font-heading text-2xl font-black leading-none">Groot <span className="text-brand-orange">ONE</span></p>
            <p className="mt-1 text-xs uppercase tracking-[0.25em] text-slate-400">Sıradaki Kasa{d.store_name ? ` · ${d.store_name}` : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="board-store-filter" value={loc} onChange={(e) => setLoc(e.target.value)}
            className="rounded-md border border-white/15 bg-slate-800 px-3 py-2 text-sm text-white focus:outline-none">
            <option value="">Tüm Mağazalar</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div data-testid="board-available" className="rounded-full bg-brand-green/15 px-3 py-2 text-sm font-semibold text-emerald-300">{d.available} müsait</div>
          <div data-testid="board-busy" className="rounded-full bg-amber-500/15 px-3 py-2 text-sm font-semibold text-amber-300">{d.busy} işlemde</div>
        </div>
      </header>
      <main className="flex flex-1 flex-col gap-6 p-8">
        {loc && (
          <div data-testid="board-waiting" className="flex items-center justify-center gap-6 rounded-2xl bg-white/[0.04] p-5 ring-1 ring-inset ring-white/10">
            <span className="inline-flex items-center gap-2 text-lg font-semibold text-slate-300"><Users className="h-6 w-6 text-brand-orange" /> Bekleyen Müşteri</span>
            <button data-testid="board-waiting-dec" onClick={() => adjustWaiting(-1)} className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/20"><Minus className="h-6 w-6" /></button>
            <span data-testid="board-waiting-count" className="tabular font-heading text-5xl font-black text-brand-orange">{d.waiting ?? 0}</span>
            <button data-testid="board-waiting-inc" onClick={() => adjustWaiting(1)} className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/20 hover:bg-brand-orange/30"><Plus className="h-6 w-6 text-brand-orange" /></button>
          </div>
        )}
        {(!d.checkouts || d.checkouts.length === 0) ? (
          <div className="flex flex-1 flex-col items-center justify-center text-center text-slate-500">
            <DoorClosed className="h-20 w-20" /><p className="mt-6 text-2xl font-bold">Şu an açık kasa yok</p>
          </div>
        ) : (
          <div className="grid flex-1 content-start grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4">
            {d.checkouts.map((c) => (
              <div key={c.no} data-testid={`board-checkout-${c.no}`} className={`flex flex-col items-center justify-center rounded-3xl p-8 text-center ring-2 ${c.busy ? "bg-amber-500/10 ring-amber-500/40" : "bg-brand-green/15 ring-brand-green/50"}`}>
                <p className="font-heading text-5xl font-black">{c.label}</p>
                <p className="mt-1 text-sm text-slate-300">{c.cashier_name}</p>
                {!loc && c.location_name && <p className="text-[11px] text-slate-500">{c.location_name}</p>}
                {c.busy
                  ? <p className="mt-4 rounded-full bg-amber-500/20 px-5 py-2 text-xl font-black text-amber-300">MEŞGUL</p>
                  : <p className="mt-4 animate-pulse rounded-full bg-brand-green/25 px-5 py-2 text-xl font-black text-emerald-300">BUYRUN ✓</p>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function CustomerDisplay() {
  const k = new URLSearchParams(window.location.search).get("k");
  if (k === "genel") return <CheckoutBoard />;
  return <RegisterDisplay />;
}
